// Hooks e contextos de fazenda — extraído de App.jsx — Fase C
import { DB } from "../lib/deviceStorage";
import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { GLOBAL_KEY } from "../config/appConfig";
import { getLiveCollectionCache, setLiveCollectionCache } from "../sync/cacheApi";
import { pushGlobal } from "../sync/collectionApi";
import { sanitizeGlobalCollectionForView } from "../lib/conflictUtils";
import { migrateCollectionRecordNumbers, supportsRecordNumberMigration } from "../lib/recordNumberMigration";
import { makeCreateMeta } from "../lib/meta";
import { makeSeed } from "../lib/seed";
import { createClientId } from "../lib/formatters";
import { parseSyncTs } from "../lib/syncUtils";
import { DEFAULT_VISIBLE_FARM_SPECIES, ANIMAL_SPECIES } from "../lib/globals";
import { getActiveFarmGeneration, stampFarmCollectionGenerations, filterFarmCollectionByActiveGeneration, mergeVisibleFarmCollectionWithHiddenGenerationRecords, shouldCreateGenerationConflict, buildGenerationConflictRecord } from "../lib/farmGenerations";

function normalizeFarmSpeciesList(list) {
  const seen = new Set();
  const out = [];
  for (const item of Array.isArray(list) ? list : []) {
    const value = String(item || "").trim();
    if (!value || !ANIMAL_SPECIES.includes(value) || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

const FarmCtx = createContext({ fid:"" });
const useFarm = () => useContext(FarmCtx);

const NATIVE_DEVICE_REVOKED_PURGE_START_EVENT = "fz:native-device-revoked-purge-start";
const NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT = "fz:native-device-revoked-purge";
const SENSITIVE_GLOBAL_COLLECTIONS_ON_NATIVE_PURGE = new Set(["stock", "people", "suppliers", "providers", "animalBuyers"]);


function getSessionActorName() {
  try {
    const raw = localStorage.getItem("fz:session");
    const session = raw ? JSON.parse(raw) : null;
    return String(session?.name || session?.username || session?.role || "sistema").trim() || "sistema";
  } catch {
    return "sistema";
  }
}

function buildAdminGenerationConflictRecipients(accounts) {
  const recipientsMap = new Map();
  const pushRecipient = (acc, roleOverride = "admin") => {
    if (!acc || acc.active === false) return;
    const role = String(roleOverride || acc?.role || "").trim().toLowerCase();
    if (!["admin", "gestor"].includes(role)) return;
    const accountId = String(acc?.id || acc?.accountId || "").trim();
    const userId = String(acc?.userId || acc?.employeeId || "").trim();
    const username = String(acc?.username || acc?.login || acc?.name || "").trim();
    const key = accountId || userId || username;
    if (!key || recipientsMap.has(key)) return;
    recipientsMap.set(key, { accountId, userId, username, role, name: String(acc?.name || username || role).trim() });
  };
  (Array.isArray(accounts) ? accounts : []).forEach((acc) => {
    const role = String(acc?.role || "").trim().toLowerCase();
    if (role === "admin" || role === "gestor") pushRecipient(acc, role);
  });
  return Array.from(recipientsMap.values());
}

async function loadGenerationConflictResolutionMap(fid) {
  const key = `fz:${fid}:generationConflictResolutions`;
  try {
    const fromDb = await DB.get(key);
    if (fromDb && typeof fromDb === "object" && !Array.isArray(fromDb)) return fromDb;
  } catch {}
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch {}
  return {};
}

async function registerGenerationConflictsForCollection({ fid, farm, collection, rawList }) {
  if (!fid || !farm || !collection || collection === "generationConflicts" || collection === "notifications") return;
  const candidates = (Array.isArray(rawList) ? rawList : []).filter((item) => shouldCreateGenerationConflict(item, farm, collection));
  if (!candidates.length) return;
  const actor = getSessionActorName();
  let deviceId = "";
  try { deviceId = localStorage.getItem("fz:device_id") || ""; } catch {}
  const now = new Date().toISOString();
  const conflictKey = `fz:${fid}:generationConflicts`;
  const notificationsKey = `fz:${fid}:notifications`;
  const resolutionMap = await loadGenerationConflictResolutionMap(fid);
  const existingConflicts = (await DB.get(conflictKey)) || [];
  const existingByKey = new Map((Array.isArray(existingConflicts) ? existingConflicts : []).map((item) => [String(item?.conflictKey || item?.id || ""), item]));
  const newConflicts = [];
  for (const record of candidates) {
    const conflict = buildGenerationConflictRecord({ farm, collection, record, actor, deviceId, now });
    const key = String(conflict?.conflictKey || conflict?.id || "");
    if (!key) continue;
    const resolved = resolutionMap?.[key];
    if (resolved && String(resolved?.status || "").trim() && String(resolved.status) !== "pending") continue;
    const existing = existingByKey.get(key);
    if (existing) {
      const existingStatus = String(existing?.status || "pending").trim() || "pending";
      if (existingStatus !== "pending") {
        resolutionMap[key] = {
          status: existingStatus,
          reviewedAt: existing?.reviewedAt || existing?.updatedAt || now,
          reviewedBy: existing?.reviewedBy || existing?.updatedBy || actor,
        };
      }
      continue;
    }
    existingByKey.set(key, conflict);
    newConflicts.push(conflict);
  }
  try {
    const resolvedEntries = Object.entries(resolutionMap || {}).filter(([, value]) => value && String(value?.status || "") !== "pending");
    if (resolvedEntries.length) {
      const resolutionKey = `fz:${fid}:generationConflictResolutions`;
      const compactResolutionMap = Object.fromEntries(resolvedEntries);
      await DB.set(resolutionKey, compactResolutionMap);
      try { localStorage.setItem(resolutionKey, JSON.stringify(compactResolutionMap)); } catch {}
    }
  } catch {}
  if (!newConflicts.length) return;
  try {
    const alertKey = `fz:generationConflict:userNotice:${fid}:${newConflicts.map(c => c.conflictKey).join("|")}`;
    if (!localStorage.getItem(alertKey)) {
      localStorage.setItem(alertKey, now);
      setTimeout(() => {
        try {
          window.alert("A fazenda foi reiniciada enquanto este dispositivo estava offline. Os dados lançados pertencem à geração anterior. Entre em contato com o administrador para a adequação dos dados para a nova geração.");
        } catch {}
      }, 120);
    }
  } catch {}
  const mergedConflicts = [...(Array.isArray(existingConflicts) ? existingConflicts : []), ...newConflicts];
  await DB.set(conflictKey, mergedConflicts);
  setLiveCollectionCache(conflictKey, mergedConflicts);
  try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: conflictKey, source: "generation-conflict" } })); } catch {}
  try { window.dispatchEvent(new CustomEvent("fz:generation-conflict:detected", { detail: { farmId: fid, count: newConflicts.length, collections: ["generationConflicts"] } })); } catch {}

  const accounts = (await DB.get("auth:accounts")) || [];
  const recipients = buildAdminGenerationConflictRecipients(accounts);
  if (!recipients.length) return;
  const existingNotifications = (await DB.get(notificationsKey)) || [];
  const existingNotificationKeys = new Set((Array.isArray(existingNotifications) ? existingNotifications : []).map((item) => String(item?.generationConflictKey || "")).filter(Boolean));
  const notificationsToAdd = newConflicts
    .filter((conflict) => !existingNotificationKeys.has(String(conflict?.conflictKey || "")))
    .map((conflict) => {
      const notifId = createClientId("ntf");
      return {
        id: notifId,
        clientId: notifId,
        farmId: String(fid),
        type: "generation_conflict",
        event: "generation_conflict_detected",
        eventLabel: "Conflito de geração detectado",
        title: "Conflito de geração detectado",
        summary: `Dados da geração ${conflict.originGeneration} chegaram após a fazenda estar na geração ${conflict.activeGeneration}.`,
        body: "A fazenda foi reiniciada enquanto um dispositivo estava offline. Revise em Admin > Sincronização > Conflitos de Gerações.",
        entityType: "generation_conflict",
        entityId: conflict.id,
        generationConflictKey: conflict.conflictKey,
        notificationModuleId: "sync",
        notificationSectionKey: "generation_conflicts",
        recipients,
        openedInApp: false,
        readBy: [],
        eventAt: now,
        createdAt: now,
        createdBy: actor,
        updatedAt: now,
        updatedBy: actor,
      };
    });
  if (!notificationsToAdd.length) return;
  const mergedNotifications = [...(Array.isArray(existingNotifications) ? existingNotifications : []), ...notificationsToAdd];
  await DB.set(notificationsKey, mergedNotifications);
  setLiveCollectionCache(notificationsKey, mergedNotifications);
  try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: notificationsKey, source: "generation-conflict" } })); } catch {}
}

function useGlobalCollection(collection, seed=[]) {
  const key = GLOBAL_KEY(collection);
  const initialRaw = (() => {
    try {
      const live = getLiveCollectionCache(key);
      if (Array.isArray(live)) return live;
    } catch {}
    try {
      const stored = localStorage.getItem(key);
      const parsed = stored ? JSON.parse(stored) : null;
      return Array.isArray(parsed) ? parsed : (seed||[]);
    } catch {
      return seed||[];
    }
  })();
  const [data, _setData] = useState(() => sanitizeGlobalCollectionForView(collection, initialRaw));
  const rawRef = useRef(Array.isArray(initialRaw) ? initialRaw : (initialRaw ?? []));

  function applyVisibleData(rawValue) {
    rawRef.current = Array.isArray(rawValue) ? rawValue : (rawValue ?? []);
    try { setLiveCollectionCache(key, rawRef.current); } catch {}
    _setData(sanitizeGlobalCollectionForView(collection, rawRef.current));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    async function reload() {
      try {
        const live = getLiveCollectionCache(key);
        if (!cancelled && Array.isArray(live)) {
          applyVisibleData(live);
          return;
        }
      } catch {}
      try {
        const fromDb = await DB.get(key);
        if (!cancelled && Array.isArray(fromDb)) {
          applyVisibleData(fromDb);
          return;
        }
      } catch {}
      try {
        const stored = localStorage.getItem(key);
        const parsed = stored ? JSON.parse(stored) : null;
        if (!cancelled && Array.isArray(parsed)) applyVisibleData(parsed);
      } catch {}
    }
    const handler = (e) => {
      if (!e.detail?.key || e.detail.key === key) reload();
    };
    const onNativeRevokedPurge = () => {
      if (!SENSITIVE_GLOBAL_COLLECTIONS_ON_NATIVE_PURGE.has(collection)) return;
      rawRef.current = [];
      try { setLiveCollectionCache(key, []); } catch {}
      _setData([]);
    };
    reload();
    window.addEventListener("fz:sync:updated", handler);
    window.addEventListener(NATIVE_DEVICE_REVOKED_PURGE_START_EVENT, onNativeRevokedPurge);
    window.addEventListener(NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT, onNativeRevokedPurge);
    return () => {
      cancelled = true;
      window.removeEventListener("fz:sync:updated", handler);
      window.removeEventListener(NATIVE_DEVICE_REVOKED_PURGE_START_EVENT, onNativeRevokedPurge);
      window.removeEventListener(NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT, onNativeRevokedPurge);
    };
  }, [key, collection]); // eslint-disable-line react-hooks/exhaustive-deps

  function save(newData) {
    const prev = Array.isArray(rawRef.current) ? rawRef.current : (rawRef.current ?? []);
    const resolvedData = typeof newData === 'function' ? newData(prev) : newData;
    const rawNext = Array.isArray(resolvedData) ? resolvedData : (resolvedData ?? []);
    rawRef.current = rawNext;
    applyVisibleData(rawNext);
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", {detail:{key}})); } catch {}
    try { DB.set(key, rawNext).catch(() => {}); } catch {}
    if (navigator.onLine) {
      (async () => {
        try {
          const res = await pushGlobal(key, rawNext, "global-save");
          if (res?.version) await DB.set(`${key}:_ver`, res.version);
        } catch(e) {}
      })();
    }
  }

  return [data, save];
}


function resolveLatestFarmGenerationSnapshot(farm) {
  try {
    const fid = String(farm?.id || "").trim();
    if (!fid) return farm;
    const candidates = [];
    try {
      const rawActive = localStorage.getItem("fz:activeFarm");
      const active = rawActive ? JSON.parse(rawActive) : null;
      if (String(active?.id || "") === fid) candidates.push(active);
    } catch {}
    try {
      const farmsRaw = localStorage.getItem("fz:farms");
      const farms = farmsRaw ? JSON.parse(farmsRaw) : null;
      const match = Array.isArray(farms) ? farms.find(item => String(item?.id || "") === fid) : null;
      if (match) candidates.push(match);
    } catch {}
    const baseGeneration = getActiveFarmGeneration(farm);
    let best = farm;
    let bestGeneration = baseGeneration;
    for (const candidate of candidates) {
      const generation = getActiveFarmGeneration(candidate);
      if (generation > bestGeneration) {
        best = { ...(farm || {}), ...(candidate || {}), activeDataGeneration: generation, farmDataGeneration: generation };
        bestGeneration = generation;
      }
    }
    return best;
  } catch {
    return farm;
  }
}


function useFarmCollection(collection, seed) {
  const { fid, farm } = useFarm();
  const effectiveFarm = resolveLatestFarmGenerationSnapshot(farm);
  const key = `fz:${fid}:${collection}`;
  const [data, _set] = useState([]);
  const dataRef = useRef([]);
  const rawRef = useRef([]);
  const activeGeneration = getActiveFarmGeneration(effectiveFarm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dataRef.current = Array.isArray(data) ? data : (data ?? []);
  }, [data]);

  function protectOrdersAnalysisHydrationDowngrade(existingList, incomingList) {
    if (collection !== "orders") return incomingList;
    const current = Array.isArray(existingList) ? existingList : [];
    const incoming = Array.isArray(incomingList) ? incomingList : [];
    if (!current.length || !incoming.length) return incoming;
    const byId = new Map();
    current.forEach((record) => {
      const key = String(record?.clientId || record?.id || "").trim();
      if (key) byId.set(key, record);
    });
    if (!byId.size) return incoming;
    let changed = false;
    const guarded = incoming.map((record) => {
      const key = String(record?.clientId || record?.id || "").trim();
      const existing = key ? byId.get(key) : null;
      if (!existing || !record) return record;
      const existingStatus = String(existing?.status || "").trim();
      const incomingStatus = String(record?.status || "").trim();
      const existingHasAnalysis = !!(existing?.firstRecipientOpenedAt || existing?.analysisStartedAt);
      const incomingHasAnalysis = !!(record?.firstRecipientOpenedAt || record?.analysisStartedAt);
      if (existingStatus === "Em Análise" && existingHasAnalysis && ["Pendente", "Criado"].includes(incomingStatus) && !incomingHasAnalysis) {
        changed = true;
        return {
          ...record,
          status: existing.status,
          firstRecipientOpenedAt: existing.firstRecipientOpenedAt,
          firstRecipientOpenedBy: existing.firstRecipientOpenedBy,
          firstRecipientOpenedByAccountId: existing.firstRecipientOpenedByAccountId,
          firstRecipientOpenedByUsername: existing.firstRecipientOpenedByUsername,
          firstRecipientOpenedByRole: existing.firstRecipientOpenedByRole,
          firstRecipientOpenedDeviceId: existing.firstRecipientOpenedDeviceId,
          analysisStartedAt: existing.analysisStartedAt,
          analysisStartedBy: existing.analysisStartedBy,
          analysisStartedByAccountId: existing.analysisStartedByAccountId,
          analysisStartedByUsername: existing.analysisStartedByUsername,
          analysisStartedDeviceId: existing.analysisStartedDeviceId,
        };
      }
      return record;
    });
    return changed ? guarded : incoming;
  }

  async function applyVisibleData(rawValue) {
    const rawList = Array.isArray(rawValue) ? rawValue : (rawValue ?? []);
    rawRef.current = rawList;
    registerGenerationConflictsForCollection({ fid, farm: effectiveFarm, collection, rawList }).catch(() => {});
    const activeList = filterFarmCollectionByActiveGeneration(rawList, effectiveFarm, collection);
    const visible = await sanitizeGlobalCollectionForView(collection, activeList);
    const safeVisible = protectOrdersAnalysisHydrationDowngrade(dataRef.current, Array.isArray(visible) ? visible : (visible ?? []));
    dataRef.current = safeVisible;
    _set(dataRef.current);
  }

  async function loadFromStorage(options = {}) {
    if(!fid) return;
    // v3.5.06: debounce de 100ms — evita triple-fire quando pós-ACK operacional
    // dispara fz:sync:updated + fz:collection:hydrated + fz:services:hydrated
    // ao mesmo tempo. Os 3 eventos chegam juntos; só o primeiro deve rodar.
    // v3.8.07: ACK operacional precisa poder furar o debounce, pois a gravação
    // local do UDT e a limpeza do ACK podem acontecer em sequência curta no app
    // nativo. Sem force, o device autor só enxerga a limpeza ao sair/entrar.
    const force = options?.force === true;
    const nowMs = Date.now();
    if (!force && loadFromStorage._lastAt && nowMs - loadFromStorage._lastAt < 100) return;
    loadFromStorage._lastAt = nowMs;
    const preferLive = !!options?.preferLive;
    if (preferLive) {
      const live = getLiveCollectionCache(key);
      if (live !== undefined) {
        await applyVisibleData(live);
        return;
      }
    }
    const d = await DB.get(key);
    const next = d ?? seed;
    const migration = supportsRecordNumberMigration(collection) ? migrateCollectionRecordNumbers(collection, next) : null;
    const rawNext = migration?.changed ? migration.records : (Array.isArray(next) ? next : (next ?? []));
    if (migration?.changed) {
      await DB.set(key, rawNext);
    }
    setLiveCollectionCache(key, rawNext);
    await applyVisibleData(rawNext);
    if(!d && fid && fid !== "") DB.set(key, seed);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadFromStorage();
  }, [key, activeGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    function shouldPreferLiveForHydration(detail = {}) {
      if (detail?.preferLive === true) return true;
      const src = String(detail?.source || "");
      return [
        "local-write",
        "operational-delta",
        "operational-delta-applied",
        "operational-bootstrap",
        "orders-operational-hydrate",
        "services-operational-hydrate",
        "operational-ack",
        "live-service-hydrate",
        "remote-delta",
      ].includes(src);
    }
    function onSyncUpdate(e) {
      if (!(e.detail && e.detail.key === key)) return;
      if (shouldPreferLiveForHydration(e.detail)) {
        loadFromStorage({ preferLive: true, force: String(e.detail?.source || "") === "operational-ack" });
        return;
      }
      loadFromStorage();
    }
    function onCollectionHydrated(e) {
      if (!(e.detail && e.detail.key === key)) return;
      loadFromStorage({ preferLive: true, force: String(e.detail?.source || "") === "operational-ack" });
    }
    function onActiveFarmGenerationUpdated(e) {
      if (String(e?.detail?.farmId || "") !== String(fid || "")) return;
      loadFromStorage({ preferLive: true });
    }
    function onNativeRevokedPurge(e) {
      const purgedFarmId = String(e?.detail?.farmId || "").trim();
      if (purgedFarmId && String(fid || "") && purgedFarmId !== String(fid || "")) return;
      rawRef.current = [];
      dataRef.current = [];
      try { setLiveCollectionCache(key, []); } catch {}
      _set([]);
    }
    window.addEventListener("fz:sync:updated", onSyncUpdate);
    window.addEventListener("fz:collection:hydrated", onCollectionHydrated);
    window.addEventListener("fz:orders:hydrated", onCollectionHydrated);
    window.addEventListener("fz:services:hydrated", onCollectionHydrated);
    window.addEventListener("fz:occurrences:hydrated", onCollectionHydrated);
    window.addEventListener("fz:activeFarm:generation-updated", onActiveFarmGenerationUpdated);
    window.addEventListener(NATIVE_DEVICE_REVOKED_PURGE_START_EVENT, onNativeRevokedPurge);
    window.addEventListener(NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT, onNativeRevokedPurge);
    return () => {
      window.removeEventListener("fz:sync:updated", onSyncUpdate);
      window.removeEventListener("fz:collection:hydrated", onCollectionHydrated);
      window.removeEventListener("fz:orders:hydrated", onCollectionHydrated);
      window.removeEventListener("fz:services:hydrated", onCollectionHydrated);
      window.removeEventListener("fz:occurrences:hydrated", onCollectionHydrated);
      window.removeEventListener("fz:activeFarm:generation-updated", onActiveFarmGenerationUpdated);
      window.removeEventListener(NATIVE_DEVICE_REVOKED_PURGE_START_EVENT, onNativeRevokedPurge);
      window.removeEventListener(NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT, onNativeRevokedPurge);
    };
  }, [key, activeGeneration, fid]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = useCallback(fn => {
    const prev = Array.isArray(dataRef.current) ? dataRef.current : (dataRef.current ?? []);
    const next = typeof fn==="function" ? fn(prev) : fn;
    const migration = supportsRecordNumberMigration(collection) ? migrateCollectionRecordNumbers(collection, next) : null;
    const migratedNext = migration?.changed ? migration.records : (Array.isArray(next) ? next : (next ?? []));
    const mergedWithHidden = mergeVisibleFarmCollectionWithHiddenGenerationRecords(migratedNext, rawRef.current, effectiveFarm, collection);
    const rawNext = stampFarmCollectionGenerations(mergedWithHidden, rawRef.current, effectiveFarm, { collection, source: navigator.onLine ? "active-farm" : "offline-active-farm" });
    rawRef.current = rawNext;
    setLiveCollectionCache(key, rawNext);
    const activeRawNext = filterFarmCollectionByActiveGeneration(rawNext, effectiveFarm, collection);
    Promise.resolve(sanitizeGlobalCollectionForView(collection, activeRawNext)).then((visibleNext) => {
      dataRef.current = Array.isArray(visibleNext) ? visibleNext : (visibleNext ?? []);
      _set(dataRef.current);
    }).catch(() => {
      dataRef.current = activeRawNext;
      _set(activeRawNext);
    });
    Promise.resolve(DB.set(key, rawNext)).finally(() => {
      try {
        window.dispatchEvent(new CustomEvent("fz:sync:updated", {detail:{key, source:"local-write"}}));
      } catch {}
    });
    return rawNext;
  }, [key, collection, effectiveFarm]);
  return [data, set];
}

// ═══════════════════════════════════════════════════════════════════════════════
// FARMS LIST
// ═══════════════════════════════════════════════════════════════════════════════

function normalizeFarmRecord(farm, actorName = "sistema") {
  const base = farm || {};
  const hasCreateMeta = !!base?.createdAt;
  const enabledAnimalSpecies = normalizeFarmSpeciesList(base?.enabledAnimalSpecies || base?.animalSpeciesEnabled || DEFAULT_VISIBLE_FARM_SPECIES);
  const activeDataGeneration = getActiveFarmGeneration(base);
  return {
    ...base,
    id: String(base?.id || createClientId("farm")),
    name: String(base?.name || "Fazenda").trim() || "Fazenda",
    shortCode: String(base?.shortCode || base?.sigla || base?.farmShortCode || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5),
    sigla: String(base?.shortCode || base?.sigla || base?.farmShortCode || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5),
    activeDataGeneration,
    farmDataGeneration: activeDataGeneration,
    generationStartedAt: base?.generationStartedAt || base?.createdAt || new Date().toISOString(),
    generationStartedBy: base?.generationStartedBy || base?.createdBy || actorName || "sistema",
    previousGenerations: Array.isArray(base?.previousGenerations) ? base.previousGenerations : [],
    emoji: base?.emoji || "🌾",
    emojiType: String(base?.emojiType || "").trim(),
    color: base?.color || "#3B82F6",
    enabledAnimalSpecies,
    _deleted: !!(base?._deleted || base?.deletedAt || base?.deleted_at),
    deletedAt: base?.deletedAt || base?.deleted_at || "",
    deleted_at: base?.deleted_at || base?.deletedAt || "",
    deletedBy: base?.deletedBy || "",
    isArchived: !!(base?.isArchived || base?.archivedAt),
    archivedAt: base?.archivedAt || "",
    archivedBy: base?.archivedBy || "",
    archiveReason: base?.archiveReason || "",
    ...(hasCreateMeta ? {} : makeCreateMeta(actorName)),
    updatedAt: base?.updatedAt || base?.createdAt || new Date().toISOString(),
    updatedBy: base?.updatedBy || actorName || "",
  };
}

function normalizeFarmsCollection(list, actorName = "sistema") {
  const out = [];
  const keyToIndex = new Map();
  const normalizeName = (v) => String(v || "").trim().toLowerCase();
  const chooseBetter = (a, b) => {
    const aDeleted = !!(a?._deleted || a?.deletedAt || a?.deleted_at);
    const bDeleted = !!(b?._deleted || b?.deletedAt || b?.deleted_at);
    const aTs = parseSyncTs(a?.deletedAt || a?.deleted_at || a?.updatedAt || a?.createdAt || "");
    const bTs = parseSyncTs(b?.deletedAt || b?.deleted_at || b?.updatedAt || b?.createdAt || "");
    let pickB = false;
    if (aDeleted !== bDeleted) pickB = bTs > aTs || (bTs === aTs && bDeleted);
    else pickB = bTs > aTs || (!a?.name && !!b?.name);
    const base = pickB ? { ...a, ...b } : { ...b, ...a };
    return normalizeFarmRecord({
      ...base,
      id: String(base?.id || a?.id || b?.id || createClientId("farm")),
    }, actorName);
  };

  for (const item of Array.isArray(list) ? list : []) {
    const farm = normalizeFarmRecord(item, actorName);
    const keys = [...new Set([String(farm.id || "").trim(), normalizeName(farm.name)].filter(Boolean))];
    const hits = [...new Set(keys.map(k => keyToIndex.get(k)).filter(i => i !== undefined))];
    if (!hits.length) {
      const idx = out.push(farm) - 1;
      keys.forEach(k => keyToIndex.set(k, idx));
      continue;
    }
    const targetIdx = hits[0];
    let merged = chooseBetter(out[targetIdx], farm);
    for (let i = 1; i < hits.length; i++) {
      const dupIdx = hits[i];
      if (dupIdx === targetIdx || !out[dupIdx]) continue;
      merged = chooseBetter(merged, out[dupIdx]);
      out[dupIdx] = null;
    }
    out[targetIdx] = merged;
    [...new Set([String(merged.id || "").trim(), normalizeName(merged.name)].filter(Boolean))].forEach(k => keyToIndex.set(k, targetIdx));
  }

  return out.filter(Boolean);
}

function useFarms() {
  const [farms, _set] = useState([]);

  function loadFarms() {
    DB.get("fz:farms").then(async d => {
      if (Array.isArray(d) && d.length > 0) {
        const normalized = normalizeFarmsCollection(d);
        if (JSON.stringify(normalized) !== JSON.stringify(d)) {
          await DB.set("fz:farms", normalized);
          try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: "fz:farms", source: "local-write" } })); } catch {}
        }
        _set(normalized);
        return;
      }
      if (navigator.onLine) {
        _set([]);
        return;
      }
      const def = [{ id:"farm1", name:"Fazenda Principal", emoji:"🌾", emojiType:"", color:"#3B82F6", enabledAnimalSpecies:[...DEFAULT_VISIBLE_FARM_SPECIES], ...makeCreateMeta("sistema") }];
      const normalized = normalizeFarmsCollection(def, "sistema").map(f => String(f.id) === "farm1" ? { ...f, id: "farm1" } : f);
      await DB.set("fz:farms", normalized);
      const seed = makeSeed();
      Object.entries(seed).forEach(([k,v]) => DB.set(`fz:farm1:${k}`, v));
      _set(normalized);
    });
  }

  useEffect(() => {
    loadFarms();
    // Re-read whenever farms are updated (sync, new farm created, farm renamed)
    function onUpdate(e) {
      if (!e.detail?.key || e.detail.key === "fz:farms") loadFarms();
    }
    window.addEventListener("fz:sync:updated", onUpdate);
    window.addEventListener("fz:farms:updated", loadFarms);
    return () => {
      window.removeEventListener("fz:sync:updated", onUpdate);
      window.removeEventListener("fz:farms:updated", loadFarms);
    };
  }, []);

  const set = useCallback(fn => {
    _set(prev => {
      const rawNext = typeof fn==="function" ? fn(prev) : fn;
      const next = normalizeFarmsCollection(rawNext);
      DB.set("fz:farms", next).finally(() => {
        try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: "fz:farms", source: "local-write" } })); } catch {}
        window.dispatchEvent(new CustomEvent("fz:farms:updated"));
      });
      return next;
    });
  }, []);

  return [farms, set];
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED UI — inputStyle importado de ./components/ui
// ═══════════════════════════════════════════════════════════════════════════════

export {
  FarmCtx,
  useFarm,
  useGlobalCollection,
  useFarmCollection,
  normalizeFarmRecord,
  normalizeFarmsCollection,
  useFarms,
  normalizeFarmSpeciesList,
};
